import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import cx from 'classnames';
import { dataDogInit } from 'helpers/dataDog';
import { isRtlLanguage } from 'helpers/isRtlLanguage';
import { CDN_TYPE_CMS, CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'utils';

import {
  AnalyticsManager,
  ApiManager,
  Atlas,
  ExtensionsManager,
  initDebug,
  isNotEmptyObject,
  Logger,
  printMissingConfigError,
  SettingsManager,
  SiteConfigManager,
  TranslationProvider,
  useSettings,
} from '@atlas/core';
import { Label } from '@atlas/ui';

import { LangSwitchWithoutRouter } from 'components/LangSwitch/LangSwitch';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';

const ShareCss = React.lazy(() => import('modules/share-css'));

const SuspenseCssComponent = () => (
  <Suspense fallback={<span style={{ display: 'none' }} />}>
    <ShareCss />
  </Suspense>
);

dataDogInit();
initDebug();

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

// Initializing api default headers
const defaultHeaders = {
  device: 'desktop',
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

SiteConfigManager.device = 'desktop';
// Setting application version & device type
ApiManager.setDefaultHeaders(defaultHeaders);

// Resolving stored settings
SettingsManager.resolveSettings();

const { host, protocol } = window.location;

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

AnalyticsManager.init({});

// Creating atlas extensions manager
window.aem = new ExtensionsManager();

const Brand = () => {
  const { theme } = useSettings();
  const metaTitle = SiteConfigManager.getConfig('config.config.seo.default_meta_title', '');

  const logoImgSrc = React.useMemo(() => {
    const cdnPathName = getCDNUrl(CDN_TYPE_CMS);
    try {
      const logoName = `logo_desktop_${theme || SYS_CONFIG.defaultTheme || 'dark'}`;
      const logo = SiteConfigManager.getConfig(logoName);

      if (!logo) {
        printMissingConfigError(logoName);
      }

      return `${cdnPathName}/${logo}`;
    } catch (error) {
      Logger.error('unable to load logo', error);

      return '';
    }
  }, [theme]);

  return (
    <div className="logo">
      <img alt={metaTitle} className="logo__img" src={logoImgSrc} />
    </div>
  );
};

const WelcomeView = () => {
  const { lang } = useSettings();

  const continueHref = React.useMemo(() => {
    if (window.SYS_CONFIG && window.SYS_CONFIG.cxfrom) {
      const cxfrom = window.SYS_CONFIG.cxfrom;

      return cxfrom.startsWith('/') ? cxfrom : `/${cxfrom}`;
    } else {
      return '/';
    }
  }, []);

  React.useEffect(() => {
    document.body.className = cx({ rtl: isRtlLanguage({ lang }) });
  }, [lang]);

  return (
    <div className="page">
      <div className="welcome-container">
        <div className="panel medium">
          <Brand />

          <div className="content">
            <Label className="welcome-title title-big-bold-text" message="welcome_page_title" />
            <Label className="welcome-message sub-title-text" message="welcome_page_message" />
          </div>

          <a className="welcome-button btn medium primary big block" href={continueHref}>
            <Label message="welcome_page_button" />
          </a>
        </div>
      </div>

      <div className="floating-top right">
        <LangSwitchWithoutRouter isSmall />
      </div>
    </div>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      <TranslationProvider>
        <WelcomeView />
        <DebugInfo />
      </TranslationProvider>
    </Atlas>
  </>,
);
